import React, { useEffect, useMemo, useState } from 'react';
import { ActivityIndicator, RefreshControl, ScrollView, Text, TouchableOpacity, View } from 'react-native';
import { DateTime } from 'luxon';
import { useLocalSearchParams, useNavigation, useRouter } from 'expo-router';
import { StyleSheet, useUnistyles } from 'react-native-unistyles';

import AnalyticsDateRangeControls from '@/components/Charts/AnalyticsDateRangeControls';
import Chart from '@/components/Charts/Chart';
import KPICard from '@/components/Charts/KPICard';
import Avatar from '@/components/People/Avatar';
import Callout from '@/components/General/Callout';
import CutCard from '@/components/General/CutCard';
import PageTitle from '@/components/General/PageTitle';
import { Table } from '@/components/General/Table';
import Unicon from '@/components/General/Unicon';
import { Query } from '@/utils/react-query/Queries';
import usePlatform from '@/utils/hooks/usePlatform';
import { dayContainer } from '@/utils/reportDates';

const DATE_PRESETS = [
    { label: 'This Week', value: 'this_week' },
    { label: 'This Month', value: 'this_month' },
    { label: 'Past 30 Days', value: 'last_30_days' },
    { label: 'Past 90 Days', value: 'last_90_days' },
    { label: 'Year to Date', value: 'year_to_date' },
    { label: 'Next 30 Days', value: 'next_30_days' },
    { label: 'Next 90 Days', value: 'next_90_days' },
];

const DUE_TYPES = [
    { label: 'Volunteer', value: 'EVENT_VOLUNTEER' },
    { label: 'Service Flow', value: 'EVENT_SERVICE_FLOW' },
    { label: 'Individual', value: 'INDIVIDUAL' },
    // Single chip covers the automations family — the stored dueType may be
    // legacy PATHS or canonical AUTOMATIONS, so the RPC filter sends both.
    { label: 'Automations', value: 'AUTOMATIONS', rpcValues: ['PATHS', 'AUTOMATIONS'] },
    { label: 'Follow Up', value: 'FOLLOW_UP' },
    { label: 'Training', value: 'TRAINING' },
];

const DUE_TYPE_CHIP_BY_PARAM = { PATHS: 'AUTOMATIONS' };

const DUE_TYPE_LABELS = Object.fromEntries(DUE_TYPES.map(type => [type.value, type.label]));

const getPresetDates = (preset, timeZone) => {
    const now = DateTime.now().setZone(timeZone || undefined);
    const day = value => dayContainer(value);
    switch (preset) {
        case 'this_week': return { start: day(now.startOf('week')), end: day(now.endOf('week')) };
        case 'this_month': return { start: day(now.startOf('month')), end: day(now.endOf('month')) };
        case 'last_30_days': return { start: day(now.minus({ days: 30 })), end: day(now) };
        case 'last_90_days': return { start: day(now.minus({ days: 90 })), end: day(now) };
        case 'year_to_date': return { start: day(now.startOf('year')), end: day(now) };
        case 'next_30_days': return { start: day(now), end: day(now.plus({ days: 30 })) };
        case 'next_90_days': return { start: day(now), end: day(now.plus({ days: 90 })) };
        default: return null;
    }
};

const responseKey = response => response === 'ACCEPTED' ? 'confirmed' : response === 'DECLINED' ? 'declined' : 'awaiting';

const SectionHeader = ({ title, subtitle }) => (
    <View style={styles.sectionHeader}>
        <View style={styles.sectionAccent} />
        <View style={styles.sectionHeaderText}>
            <Text style={styles.sectionTitle}>{title}</Text>
            <Text style={styles.sectionSubtitle}>{subtitle}</Text>
        </View>
    </View>
);

export default function VolunteerUtilizationDashboard() {
    const navigation = useNavigation();
    const router = useRouter();
    const params = useLocalSearchParams();
    const { isWeb, isNative } = usePlatform();
    const { theme: { colors, fonts } } = useUnistyles();
    const [startDate, setStartDate] = useState(null);
    const [endDate, setEndDate] = useState(null);
    const [selectedPreset, setSelectedPreset] = useState('last_90_days');
    const [selectedDueType, setSelectedDueType] = useState('EVENT_VOLUNTEER');
    const [initialized, setInitialized] = useState(false);
    const [personSort, setPersonSort] = useState({ key: 'total', direction: 'desc' });

    const { data: loggedInUser } = Query.people.loggedInUser();
    const { data: organization } = Query.organization.byId({
        id: loggedInUser?.organizationID,
        options: { enabled: !!loggedInUser?.organizationID }
    });
    const canView = loggedInUser?.isAdmin === true;
    const dueTypes = useMemo(() => {
        if (!selectedDueType) return [];
        const chip = DUE_TYPES.find(type => type.value === selectedDueType);
        return chip?.rpcValues || [selectedDueType];
    }, [selectedDueType]);
    const utilizationQuery = Query.reports.volunteerUtilization({
        startDate,
        endDate,
        dueTypes,
        options: { enabled: initialized && !!startDate && !!endDate && !!canView }
    });
    const assignments = utilizationQuery.data || [];

    useEffect(() => {
        navigation.setOptions({ headerTitle: () => <PageTitle title="Volunteer Utilization" /> });
    }, [navigation]);

    useEffect(() => {
        if (initialized || !organization?.timezone) return;
        const preset = typeof params.preset === 'string' ? params.preset : 'last_90_days';
        const dueType = typeof params.dueType === 'string' ? params.dueType : 'EVENT_VOLUNTEER';
        const customStart = typeof params.startDate === 'string' ? DateTime.fromISO(params.startDate).startOf('day').toJSDate() : null;
        const customEnd = typeof params.endDate === 'string' ? DateTime.fromISO(params.endDate).startOf('day').toJSDate() : null;
        const presetDates = getPresetDates(preset, organization.timezone) || getPresetDates('last_90_days', organization.timezone);

        const resolvedDueType = DUE_TYPE_CHIP_BY_PARAM[dueType] || dueType;
        setSelectedDueType(DUE_TYPES.some(type => type.value === resolvedDueType) ? resolvedDueType : dueType === 'ALL' ? null : 'EVENT_VOLUNTEER');
        setSelectedPreset(customStart && customEnd ? 'custom' : preset);
        setStartDate(customStart || presetDates.start);
        setEndDate(customEnd || presetDates.end);
        setInitialized(true);
    }, [initialized, organization?.timezone, params]);

    useEffect(() => {
        if (!initialized || !startDate || !endDate) return;
        const nextParams = { dueType: selectedDueType || 'ALL' };
        if (selectedPreset === 'custom') {
            nextParams.startDate = DateTime.fromJSDate(startDate).toISODate();
            nextParams.endDate = DateTime.fromJSDate(endDate).toISODate();
        } else {
            nextParams.preset = selectedPreset;
        }
        router.setParams(nextParams);
    }, [initialized, startDate, endDate, selectedPreset, selectedDueType, router]);

    const personRows = useMemo(() => {
        const people = new Map();
        assignments.forEach(assignment => {
            const current = people.get(assignment.person_id) || {
                id: assignment.person_id,
                firstName: assignment.first_name,
                lastName: assignment.last_name,
                avatarStorageKey: assignment.avatar_storage_key,
                avatarUpdatedAt: assignment.avatar_updated_at,
                positions: new Set(),
                events: new Set(),
                dueTypes: new Set(),
                confirmed: 0,
                awaiting: 0,
                declined: 0,
                total: 0,
            };
            current.positions.add(assignment.task_title);
            if (assignment.event_instance_id) current.events.add(assignment.event_name);
            current.dueTypes.add(assignment.due_type);
            current[responseKey(assignment.assignment_response)] += 1;
            current.total += 1;
            people.set(assignment.person_id, current);
        });
        const rows = Array.from(people.values()).map(person => ({
            ...person,
            positions: Array.from(person.positions).sort(),
            events: Array.from(person.events).sort(),
            dueTypes: Array.from(person.dueTypes).sort(),
        }));
        const { key, direction } = personSort;
        return rows.sort((a, b) => {
            const aValue = key === 'person' ? `${a.lastName} ${a.firstName}`.toLowerCase() : a[key] || 0;
            const bValue = key === 'person' ? `${b.lastName} ${b.firstName}`.toLowerCase() : b[key] || 0;
            if (aValue === bValue) return 0;
            return (aValue > bValue ? 1 : -1) * (direction === 'asc' ? 1 : -1);
        });
    }, [assignments, personSort]);

    const eventRows = useMemo(() => {
        const events = new Map();
        assignments.filter(assignment => assignment.event_instance_id).forEach(assignment => {
            const current = events.get(assignment.event_instance_id) || {
                id: assignment.event_instance_id,
                eventName: assignment.event_name,
                serviceTime: assignment.service_time,
                people: new Set(),
                positions: new Set(),
                confirmed: 0,
                awaiting: 0,
                declined: 0,
                total: 0,
            };
            current.people.add(assignment.person_id);
            current.positions.add(assignment.task_title);
            current[responseKey(assignment.assignment_response)] += 1;
            current.total += 1;
            events.set(assignment.event_instance_id, current);
        });
        return Array.from(events.values()).map(event => ({
            ...event,
            peopleCount: event.people.size,
            positions: Array.from(event.positions).sort(),
        })).sort((a, b) => new Date(b.serviceTime) - new Date(a.serviceTime));
    }, [assignments]);

    const summary = useMemo(() => {
        const confirmed = assignments.filter(assignment => assignment.assignment_response === 'ACCEPTED').length;
        const awaiting = assignments.filter(assignment => assignment.assignment_response === 'NO_RESPONSE').length;
        return {
            people: personRows.length,
            assignments: assignments.length,
            confirmed,
            awaiting,
            confirmationRate: assignments.length ? Math.round((confirmed / assignments.length) * 100) : 0,
            events: eventRows.length,
        };
    }, [assignments, eventRows.length, personRows.length]);

    const chartParameters = useMemo(() => ({
        startDate: startDate ? DateTime.fromJSDate(startDate).toISODate() : '',
        endDate: endDate ? DateTime.fromJSDate(endDate).toISODate() : '',
        dueTypes: selectedDueType || '',
    }), [startDate, endDate, selectedDueType]);

    const personColumns = useMemo(() => [
        {
            accessor: 'person',
            value: 'Person',
            sortable: true,
            renderer: row => (
                <View style={styles.personCell}>
                    <Avatar size={34} disableBorder profile={row} />
                    <Text style={styles.primaryCellText}>{row.firstName} {row.lastName}</Text>
                </View>
            )
        },
        {
            accessor: 'positions',
            value: 'Positions',
            renderer: row => <Text style={styles.cellText} numberOfLines={2}>{row.positions.join(', ') || '—'}</Text>
        },
        {
            accessor: 'events',
            value: 'Events',
            renderer: row => <Text style={styles.cellText} numberOfLines={2}>{row.events.join(', ') || 'No event'}</Text>
        },
        {
            accessor: 'dueTypes',
            value: 'Task type',
            renderer: row => <Text style={styles.cellText}>{row.dueTypes.map(type => DUE_TYPE_LABELS[type] || type).join(', ')}</Text>
        },
        { accessor: 'confirmed', value: 'Confirmed', sortable: true, renderer: row => <Text style={styles.statusText('success')}>{row.confirmed}</Text> },
        { accessor: 'awaiting', value: 'Awaiting', sortable: true, renderer: row => <Text style={styles.statusText('warning')}>{row.awaiting}</Text> },
        { accessor: 'declined', value: 'Declined', sortable: true, renderer: row => <Text style={styles.statusText('error')}>{row.declined}</Text> },
        { accessor: 'total', value: 'Total', sortable: true, renderer: row => <Text style={styles.primaryCellText}>{row.total}</Text> },
    ], [colors, fonts]);

    const eventColumns = useMemo(() => [
        {
            accessor: 'eventName',
            value: 'Event',
            renderer: row => (
                <View>
                    <Text style={styles.primaryCellText}>{row.eventName}</Text>
                    <Text style={styles.secondaryCellText}>{DateTime.fromISO(row.serviceTime).setZone(organization?.timezone).toFormat('MMM d, yyyy • h:mm a')}</Text>
                </View>
            )
        },
        { accessor: 'peopleCount', value: 'People', renderer: row => <Text style={styles.primaryCellText}>{row.peopleCount}</Text> },
        { accessor: 'positions', value: 'Positions', renderer: row => <Text style={styles.cellText} numberOfLines={2}>{row.positions.join(', ')}</Text> },
        { accessor: 'confirmed', value: 'Confirmed', renderer: row => <Text style={styles.statusText('success')}>{row.confirmed}</Text> },
        { accessor: 'awaiting', value: 'Awaiting', renderer: row => <Text style={styles.statusText('warning')}>{row.awaiting}</Text> },
        { accessor: 'declined', value: 'Declined', renderer: row => <Text style={styles.statusText('error')}>{row.declined}</Text> },
    ], [organization?.timezone]);

    const handlePresetChange = preset => {
        const dates = getPresetDates(preset, organization?.timezone);
        if (!dates) return;
        setSelectedPreset(preset);
        setStartDate(dates.start);
        setEndDate(dates.end);
    };

    if (loggedInUser && !canView) {
        return (
            <View style={styles.permissionState} testID="volunteer-utilization-permission-denied">
                <Callout type="warning" title="Admin required" message="Only organization admins can open volunteer utilization." />
            </View>
        );
    }

    return (
        <ScrollView
            style={styles.container}
            contentContainerStyle={styles.screen}
            contentInsetAdjustmentBehavior="automatic"
            refreshControl={<RefreshControl refreshing={utilizationQuery.isRefetching} onRefresh={utilizationQuery.refetch} />}
            testID="volunteer-utilization-screen"
        >
            <CutCard accentColor="primary" style={styles.introCard} contentStyle={styles.introCardContent}>
                <View style={styles.introIcon}>
                    <Unicon name="people-outline" size={24} colorVariant="onPrimary" />
                </View>
                <View style={styles.introCopy}>
                    <Text style={styles.introTitle}>See service load, not just signups.</Text>
                    <Text style={styles.introText}>Compare who is serving, where they serve, and whether each assignment is confirmed.</Text>
                </View>
                <View style={styles.responseLegend}>
                    <Text style={styles.legendItem('success')}>{summary.confirmed} confirmed</Text>
                    <Text style={styles.legendItem('warning')}>{summary.awaiting} awaiting</Text>
                </View>
            </CutCard>

            <AnalyticsDateRangeControls
                presets={DATE_PRESETS}
                selectedPreset={selectedPreset}
                startDate={startDate}
                endDate={endDate}
                onPresetChange={handlePresetChange}
                onStartDateChange={date => {
                    setSelectedPreset('custom');
                    setStartDate(date ? DateTime.fromJSDate(date).startOf('day').toJSDate() : date);
                }}
                onEndDateChange={date => {
                    setSelectedPreset('custom');
                    setEndDate(date ? DateTime.fromJSDate(date).startOf('day').toJSDate() : date);
                }}
                isNative={isNative}
                maximumEndDate={null}
                testIDPrefix="volunteer-utilization-date"
            >
                <View style={styles.dueTypeFilter}>
                    <Text style={styles.filterLabel}>Task type</Text>
                    <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={styles.dueTypeOptions}>
                        {[{ label: 'All', value: null }, ...DUE_TYPES].map(type => {
                            const active = selectedDueType === type.value;
                            return (
                                <TouchableOpacity
                                    key={type.value || 'ALL'}
                                    style={styles.filterPill(active)}
                                    onPress={() => setSelectedDueType(type.value)}
                                    testID={`volunteer-utilization-due-type-${type.value || 'ALL'}`}
                                    accessibilityRole="button"
                                    accessibilityState={{ selected: active }}
                                    aria-selected={active}
                                >
                                    <Text style={styles.filterPillText(active)}>{type.label}</Text>
                                </TouchableOpacity>
                            );
                        })}
                    </ScrollView>
                </View>
            </AnalyticsDateRangeControls>

            <SectionHeader title="Coverage snapshot" subtitle="Assignment readiness for the selected period" />
            <View style={styles.kpiGrid}>
                <KPICard title="People scheduled" content={utilizationQuery.isLoading ? <ActivityIndicator /> : summary.people} />
                <KPICard title="Assignments" content={utilizationQuery.isLoading ? <ActivityIndicator /> : summary.assignments} />
                <KPICard title="Confirmation rate" content={utilizationQuery.isLoading ? <ActivityIndicator /> : `${summary.confirmationRate}%`} />
                <KPICard title="Events covered" content={utilizationQuery.isLoading ? <ActivityIndicator /> : summary.events} />
            </View>

            {utilizationQuery.isError ? (
                <Callout type="danger" title="Unable to load dashboard" message="Refresh to try loading volunteer utilization again." />
            ) : null}

            <SectionHeader title="Patterns" subtitle="Load distribution and response health" />
            <View style={styles.chartGrid}>
                <Chart scrollEnabled height={isWeb ? 560 : 460} chartName="volunteers/UtilizationByPersonChart" parameters={chartParameters} />
                <Chart scrollEnabled height={isWeb ? 560 : 460} chartName="volunteers/AssignmentsByPositionChart" parameters={chartParameters} />
                <Chart height={isWeb ? 500 : 420} chartName="volunteers/AssignmentResponseTrendChart" parameters={chartParameters} />
            </View>

            <SectionHeader title="People" subtitle="Aggregated assignments, positions, and events by person" />
            <Table
                tableId="VolunteerUtilizationPeople"
                title="Volunteer utilization by person"
                description={`${personRows.length} people in selected range`}
                data={personRows}
                columns={personColumns}
                isLoading={utilizationQuery.isLoading}
                sortConfig={personSort}
                onSortChange={setPersonSort}
                onRowClick={person => router.push(`/people/${person.id}`)}
                enableCards={!isWeb}
            />

            <SectionHeader title="Events" subtitle="Where people are scheduled and current response coverage" />
            <Table
                tableId="VolunteerUtilizationEvents"
                title="Event coverage"
                description={`${eventRows.length} events in selected range`}
                data={eventRows}
                columns={eventColumns}
                isLoading={utilizationQuery.isLoading}
                onRowClick={event => router.push(`/events/instances/${event.id}`)}
                enableCards={!isWeb}
            />
        </ScrollView>
    );
}

const styles = StyleSheet.create(({ colors, fonts, shadows }) => ({
    container: { flex: 1, backgroundColor: 'transparent' },
    screen: { padding: 16, paddingBottom: 40, gap: 16 },
    permissionState: { flex: 1, padding: 16, backgroundColor: colors.background },
    // CutCard owns surface/radius; the shadow stays here so it converts
    // to a clip-path drop-shadow on web.
    introCard: {
        boxShadow: shadows.card,
        padding: 18,
        _web: {
            willChange: 'transform',
        },
    },
    introCardContent: {
        flexDirection: 'row',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: 14,
    },
    introIcon: {
        width: 48,
        height: 48,
        borderRadius: 16,
        backgroundColor: colors.primary,
        alignItems: 'center',
        justifyContent: 'center',
    },
    introCopy: { flex: 1, minWidth: 220, gap: 3 },
    introTitle: { fontFamily: fonts.heading, color: colors.text, fontSize: 18 },
    introText: { fontFamily: fonts.body, color: colors.textSecondary, fontSize: 13, lineHeight: 19 },
    responseLegend: { gap: 5, alignItems: 'flex-end' },
    legendItem: variant => ({ fontFamily: fonts.heading, color: colors[variant], fontSize: 12, fontVariant: ['tabular-nums'] }),
    sectionHeader: { flexDirection: 'row', alignItems: 'center', gap: 10, paddingTop: 6 },
    sectionAccent: { width: 4, height: 34, borderRadius: 2, backgroundColor: colors.primary },
    sectionHeaderText: { flex: 1, gap: 2 },
    sectionTitle: { fontFamily: fonts.heading, color: colors.text, fontSize: 18 },
    sectionSubtitle: { fontFamily: fonts.body, color: colors.textSecondary, fontSize: 13 },
    dueTypeFilter: { flex: 1, minWidth: 280, gap: 7 },
    filterLabel: { fontFamily: fonts.heading, color: colors.textSecondary, fontSize: 12, textTransform: 'uppercase', letterSpacing: 0.5 },
    dueTypeOptions: { flexDirection: 'row', gap: 7, paddingVertical: 2 },
    filterPill: active => ({
        paddingHorizontal: 12,
        paddingVertical: 8,
        borderRadius: 20,
        backgroundColor: active ? colors.primary : colors.background,
    }),
    filterPillText: active => ({ fontFamily: active ? fonts.heading : fonts.body, color: active ? colors.onPrimary : colors.text, fontSize: 12 }),
    kpiGrid: { flexDirection: 'row', flexWrap: 'wrap', gap: 12 },
    chartGrid: { flexDirection: 'row', flexWrap: 'wrap', gap: 12 },
    personCell: { flexDirection: 'row', alignItems: 'center', gap: 9 },
    primaryCellText: { fontFamily: fonts.heading, color: colors.text, fontSize: 13 },
    secondaryCellText: { fontFamily: fonts.body, color: colors.textSecondary, fontSize: 12, paddingTop: 2 },
    cellText: { fontFamily: fonts.body, color: colors.text, fontSize: 13 },
    statusText: variant => ({ fontFamily: fonts.heading, color: colors[variant], fontSize: 13, fontVariant: ['tabular-nums'] }),
}));
